import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  BookOpenCheck,
  CircleDot,
  GraduationCap,
  Headphones,
  HeartHandshake,
  Megaphone,
  ShieldCheck
} from 'lucide-react';
import TopBar from '../components/TopBar';
import authService from '../services/authService';
import { fetchPowerBISemanticData } from '../services/powerbiService';
import '../modules/webv2/webV2Module.css';

const formatPercent = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${(numericValue * 100).toFixed(0)}%` : 'N/A';
};

const hasVciValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0;
};

const formatVciPercent = (value) => (hasVciValue(value) ? formatPercent(value) : 'No data');

const getAverage = (rows, key) => {
  const values = rows.map((row) => Number(row?.[key])).filter((value) => hasVciValue(value));
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const getVciCountryKey = (row) => String(row?.iso3 || row?.country || '').trim().toUpperCase();
const DEMAND_TA_TEAM_NAME = 'Immunization - SBC';

const normalizeText = (value = '') => String(value || '').trim().toLowerCase();
const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
};
const findColumnKey = (columns, names) => columns.find((col) => names.includes(normalizeColumn(col))) || null;
const getRowText = (row, columnKey) => (columnKey ? String(row?.[columnKey] || '').trim() : '');
const getTaRequestKey = (row, index, titleColumnKey) => `${getRowText(row, titleColumnKey) || 'sbc-ta-request'}-${index}`;
const isDemandTaTeam = (value) => normalizeText(value).replace(/\s+/g, ' ').includes(normalizeText(DEMAND_TA_TEAM_NAME));

const taRowMatchesSelectedCountry = (row, matchValues, taCountryColumnKey, taIso3ColumnKey) => {
  if (!matchValues.size) return false;

  const candidates = [
    getRowText(row, taCountryColumnKey),
    getRowText(row, taIso3ColumnKey)
  ]
    .map(normalizeText)
    .filter(Boolean);

  return candidates.some((value) => matchValues.has(value));
};

const getVciTrend = (countryRows, metricKey, row) => {
  const currentYear = Number(row?.year);
  const currentValue = Number(row?.[metricKey]);

  if (!Number.isFinite(currentYear) || !hasVciValue(currentValue)) {
    return null;
  }

  const previousRow = countryRows
    .filter((countryRow) => {
      const year = Number(countryRow?.year);
      return Number.isFinite(year) && year < currentYear && hasVciValue(countryRow?.[metricKey]);
    })
    .sort((a, b) => Number(b.year) - Number(a.year))[0];

  if (!previousRow) return null;

  const previousValue = Number(previousRow[metricKey]);
  const delta = currentValue - previousValue;
  const deltaPoints = delta * 100;
  const status = Math.abs(deltaPoints) < 0.5 ? 'stable' : deltaPoints > 0 ? 'increasing' : 'decreasing';

  return {
    status,
    label: `${deltaPoints > 0 ? '+' : ''}${deltaPoints.toFixed(0)} pp`,
    detail: `vs ${previousRow.year}`
  };
};

const socialListeningTrainingLinks = [
  {
    key: 'scl-pathway',
    title: 'Learning Pathway: SBC Social and Community Listening',
    description: 'A structured pathway for using social and community listening in SBC programming.',
    url: 'https://agora.unicef.org/course/info.php?id=54532',
    format: 'Learning pathway',
    icon: GraduationCap
  },
  {
    key: 'talkwalker',
    title: 'Using Talkwalker for social listening',
    description: 'Webinar resource for digital social listening workflows and country office examples.',
    url: 'https://agora.unicef.org/course/info.php?id=29259',
    format: 'Webinar',
    icon: Headphones
  },
  {
    key: 'sbc-advanced',
    title: 'Learning Pathway: SBC Advanced',
    description: 'Advanced learning pathway for applying social and behaviour change approaches.',
    url: 'https://agora.unicef.org/course/info.php?id=41003',
    format: 'Learning pathway',
    icon: Megaphone
  },
  {
    key: 'sbc-basics',
    title: 'Learning Pathway: SBC Basics',
    description: 'Introductory SBC pathway for core concepts, principles, and programme application.',
    url: 'https://agora.unicef.org/course/view.php?id=41002',
    format: 'Learning pathway',
    icon: BookOpenCheck
  }
];

const DemandVaccineTrustContent = () => {
  const [vciRows, setVciRows] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [showAllVciRows, setShowAllVciRows] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCountryKey, setSelectedCountryKey] = useState('');
  const [taRows, setTaRows] = useState([]);
  const [taColumns, setTaColumns] = useState([]);
  const [isTaLoading, setIsTaLoading] = useState(true);
  const [taError, setTaError] = useState('');
  const [expandedTaRequestKeys, setExpandedTaRequestKeys] = useState({});
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
  const taDatasetId = process.env.REACT_APP_COE_TA_TRACKER_DATASET_ID || '1ad5473d-bb83-4e5b-9980-7424a841f9b6';
  const taWorkspaceId = process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
  const taDaxQuery = process.env.REACT_APP_COE_TA_TRACKER_DAX_QUERY || '';
  const taTableName = process.env.REACT_APP_COE_TA_TRACKER_TABLE_NAME || 'TARequests';

  useEffect(() => {
    let mounted = true;

    const loadVciData = async () => {
      setIsLoading(true);
      setError('');

      try {
        const idToken = await authService.getIdToken();
        const query = `
          SELECT
            v.id,
            v.country,
            v.iso3,
            v.year,
            v.children,
            v.safe,
            v.effective,
            v.beliefs,
            v.government AS trust_government,
            v.healthcare_system AS trust_healthcare
          FROM vci_data v
          ORDER BY v.year DESC, v.country ASC;
        `;

        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({ query }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`VCI data request failed (${response.status})`);
        }

        const payload = await response.json();
        const rows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);

        if (mounted) {
          setVciRows(rows);
        }
      } catch (loadError) {
        if (mounted) {
          setVciRows([]);
          setError(loadError.message || 'Unable to load VCI data.');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadVciData();

    return () => {
      mounted = false;
    };
  }, [apiBase]);

  useEffect(() => {
    let mounted = true;

    const loadTaRequests = async () => {
      setIsTaLoading(true);
      setTaError('');

      try {
        const effectiveQuery = String(taDaxQuery || '').trim() || undefined;
        const effectiveTableName = effectiveQuery ? undefined : (String(taTableName || '').trim() || undefined);
        const data = await fetchPowerBISemanticData(
          taDatasetId,
          taWorkspaceId,
          effectiveQuery,
          5000,
          effectiveTableName
        );

        if (!mounted) return;
        setTaRows(Array.isArray(data?.data) ? data.data : []);
        setTaColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (loadError) {
        if (!mounted) return;
        setTaRows([]);
        setTaColumns([]);
        setTaError('TA Hub requests are unavailable right now.');
      } finally {
        if (mounted) setIsTaLoading(false);
      }
    };

    loadTaRequests();

    return () => {
      mounted = false;
    };
  }, [taDatasetId, taWorkspaceId, taDaxQuery, taTableName]);

  const availableYears = useMemo(() => (
    Array.from(new Set(vciRows.map((row) => row.year).filter(Boolean)))
      .sort((a, b) => Number(b) - Number(a))
  ), [vciRows]);

  useEffect(() => {
    if (availableYears.length === 0) {
      if (selectedYear) setSelectedYear('');
      return;
    }

    if (!availableYears.some((year) => String(year) === String(selectedYear))) {
      setSelectedYear(String(availableYears[0]));
    }
  }, [availableYears, selectedYear]);

  const selectedYearRows = useMemo(() => (
    selectedYear
      ? vciRows.filter((row) => String(row.year) === String(selectedYear))
      : vciRows
  ), [selectedYear, vciRows]);
  const vciTableRows = showAllVciRows ? selectedYearRows : selectedYearRows.slice(0, 8);

  useEffect(() => {
    if (selectedYearRows.length === 0) {
      if (selectedCountryKey) setSelectedCountryKey('');
      return;
    }

    const countryKeys = selectedYearRows.map(getVciCountryKey).filter(Boolean);
    if (!countryKeys.includes(selectedCountryKey)) {
      setSelectedCountryKey(countryKeys[0]);
    }
  }, [selectedYearRows, selectedCountryKey]);

  const selectedCountryRow = useMemo(
    () => selectedYearRows.find((row) => getVciCountryKey(row) === selectedCountryKey) || null,
    [selectedYearRows, selectedCountryKey]
  );

  const trendByCountryYear = useMemo(() => {
    const rowsByCountry = new Map();

    vciRows.forEach((row) => {
      const countryKey = getVciCountryKey(row);
      if (!countryKey) return;
      rowsByCountry.set(countryKey, [...(rowsByCountry.get(countryKey) || []), row]);
    });

    const trendMap = new Map();

    vciRows.forEach((row) => {
      const countryKey = getVciCountryKey(row);
      if (!countryKey) return;

      const countryRows = rowsByCountry.get(countryKey) || [];
      trendMap.set(`${countryKey}-${row.year}`, {
        children: getVciTrend(countryRows, 'children', row),
        safe: getVciTrend(countryRows, 'safe', row),
        effective: getVciTrend(countryRows, 'effective', row),
        beliefs: getVciTrend(countryRows, 'beliefs', row),
        trust_government: getVciTrend(countryRows, 'trust_government', row),
        trust_healthcare: getVciTrend(countryRows, 'trust_healthcare', row)
      });
    });

    return trendMap;
  }, [vciRows]);

  const latestTrustRowByCountry = useMemo(() => {
    const latestRows = new Map();

    vciRows.forEach((row) => {
      const countryKey = getVciCountryKey(row);
      if (!countryKey) return;

      const hasTrustData = hasVciValue(row.trust_government) || hasVciValue(row.trust_healthcare);
      if (!hasTrustData) return;

      const currentLatest = latestRows.get(countryKey);
      if (!currentLatest || Number(row.year || 0) > Number(currentLatest.year || 0)) {
        latestRows.set(countryKey, row);
      }
    });

    return latestRows;
  }, [vciRows]);

  useEffect(() => {
    setShowAllVciRows(false);
  }, [selectedYear]);

  const demandTrustCards = useMemo(() => {
    const rowCount = selectedYearRows.length;
    const meta = rowCount > 0
      ? `Average across ${rowCount} country rows${selectedYear ? ` in ${selectedYear}` : ''}.`
      : 'No country data loaded for the selected year.';

    return [
      {
        key: 'children',
        value: formatPercent(getAverage(selectedYearRows, 'children')),
        title: 'Children (VaxImpChild)',
        meta,
        icon: CircleDot
      },
      {
        key: 'safe',
        value: formatPercent(getAverage(selectedYearRows, 'safe')),
        title: 'Safe (VaxSaf)',
        meta,
        icon: ShieldCheck
      },
      {
        key: 'effective',
        value: formatPercent(getAverage(selectedYearRows, 'effective')),
        title: 'Effective (VaxEff)',
        meta,
        icon: Activity
      },
      {
        key: 'beliefs',
        value: formatPercent(getAverage(selectedYearRows, 'beliefs')),
        title: 'Beliefs (VaxRel)',
        meta,
        icon: HeartHandshake
      }
    ];
  }, [selectedYear, selectedYearRows]);

  const taLeadCoeTeamColumnKey = useMemo(
    () => findColumnKey(taColumns, ['lead coe team (accountable)', 'lead coe team', 'lead coe', 'coe team']),
    [taColumns]
  );
  const taCountryColumnKey = useMemo(
    () => findColumnKey(taColumns, ['country']),
    [taColumns]
  );
  const taIso3ColumnKey = useMemo(
    () => findColumnKey(taColumns, ['iso3', 'country iso3', 'country_iso3', 'country code', 'country iso']),
    [taColumns]
  );
  const taTitleColumnKey = useMemo(
    () => findColumnKey(taColumns, [
      'request title',
      'request',
      'title',
      'activity',
      'intervention',
      'action',
      'issue'
    ]),
    [taColumns]
  );
  const taDescriptionColumnKey = useMemo(
    () => findColumnKey(taColumns, ['description', 'request description']),
    [taColumns]
  );
  const taStatusColumnKey = useMemo(
    () => findColumnKey(taColumns, ['implementation status', 'status']),
    [taColumns]
  );
  const taLeadStaffColumnKey = useMemo(
    () => findColumnKey(taColumns, ['ta lead staff', 'ta lead staff (accountable)']),
    [taColumns]
  );
  const selectedCountryMatchValues = useMemo(() => {
    const values = new Set();
    if (!selectedCountryRow) return values;
    const countryName = normalizeText(selectedCountryRow.country);
    const iso3 = normalizeText(selectedCountryRow.iso3);
    if (countryName) values.add(countryName);
    if (iso3) values.add(iso3);
    return values;
  }, [selectedCountryRow]);
  const demandTaRequests = useMemo(() => {
    const teamRows = taRows.filter((row) => isDemandTaTeam(getRowText(row, taLeadCoeTeamColumnKey)));
    if (!selectedCountryRow) return [];

    return teamRows.filter((row) => (
      taRowMatchesSelectedCountry(row, selectedCountryMatchValues, taCountryColumnKey, taIso3ColumnKey)
    ));
  }, [
    taRows,
    taLeadCoeTeamColumnKey,
    selectedCountryRow,
    selectedCountryMatchValues,
    taCountryColumnKey,
    taIso3ColumnKey
  ]);
  const selectedCountryDisplayName = selectedCountryRow?.country || selectedCountryKey;

  const toggleTaRequestExpanded = (requestKey) => {
    setExpandedTaRequestKeys((current) => ({
      ...current,
      [requestKey]: !current[requestKey]
    }));
  };

  useEffect(() => {
    setExpandedTaRequestKeys({});
  }, [selectedCountryKey]);

  const renderVciCell = (row, metricKey, showTrend = true) => {
    if (!hasVciValue(row[metricKey])) {
      return <span className="demand-vci-no-data">No data</span>;
    }

    const countryKey = getVciCountryKey(row);
    const trend = trendByCountryYear.get(`${countryKey}-${row.year}`)?.[metricKey];

    return (
      <div className="demand-vci-cell">
        <span>{formatVciPercent(row[metricKey])}</span>
        {showTrend && (
          <small className={`demand-vci-trend is-${trend?.status || 'unavailable'}`} title={trend?.detail || 'No previous data available'}>
            {trend ? `${trend.label} ${trend.detail}` : 'No data'}
          </small>
        )}
      </div>
    );
  };

  return (
    <section className="home-section vaccines-page-section demand-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <p className="vaccines-section-label">Demand & Vaccine Trust</p>
          <h2 className="vaccines-dashboard-title">Demand & Vaccine Trust overview</h2>
          <p className="vaccines-dashboard-subtitle">
            Monitor vaccine demand, confidence, acceptance, and trust-building activities across immunization programmes.
          </p>
        </div>
        <div className="budgets-toolbar" aria-label="Demand and vaccine trust filters">
          <label>
            Year
            <select
              value={selectedYear}
              onChange={(event) => setSelectedYear(event.target.value)}
              disabled={isLoading || availableYears.length === 0}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="vaccines-kpi-row demand-kpi-row">
        {demandTrustCards.map((card) => {
          const Icon = card.icon;

          return (
            <article key={card.key} className={`vaccines-kpi-card demand-kpi-${card.key}`}>
              <span className="vaccines-kpi-icon" aria-hidden="true">
                <Icon />
              </span>
              <div>
                <p className="vaccines-kpi-value">{card.value}</p>
                <p className="vaccines-kpi-label">{card.title}</p>
                <p className="vaccines-kpi-meta">{card.meta}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="vaccines-schedule-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">VCI Data</p>
            <h3 className="vaccines-card-title">Vaccine confidence indicators</h3>
          </div>
          <p className="budgets-data-note">
            {isLoading ? 'Loading VCI data...' : `${selectedYearRows.length} country rows${selectedYear ? ` for ${selectedYear}` : ''}`}
          </p>
        </div>

        {error && <p className="budgets-error-note">{error}</p>}

        <div className="vaccines-wuenic-table-wrap">
          <table className="vaccines-wuenic-table" aria-label="Demand and vaccine trust VCI data">
            <thead>
              <tr>
                <th scope="col" colSpan="3" className="demand-vci-group-spacer demand-vci-group-divider" />
                <th scope="colgroup" colSpan="5" className="demand-vci-group-header">Vaccine confidence indicators</th>
              </tr>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">
                  Trust in government
                  <span className="demand-vci-question">I trust the government</span>
                </th>
                <th scope="col" className="demand-vci-group-divider">
                  Trust in healthcare
                  <span className="demand-vci-question">I trust healthcare workers, nurses and doctors</span>
                </th>
                <th scope="col">Year</th>
                <th scope="col">
                  Children
                  <span className="demand-vci-question">I think vaccines are important for children to have</span>
                </th>
                <th scope="col">
                  Safe
                  <span className="demand-vci-question">I think vaccines are safe</span>
                </th>
                <th scope="col">
                  Effective
                  <span className="demand-vci-question">I think vaccines are effective</span>
                </th>
                <th scope="col">
                  Beliefs
                  <span className="demand-vci-question">I think vaccines are compatible with my religious beliefs</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && !error && selectedYearRows.length === 0 && (
                <tr>
                  <td colSpan="8">No VCI data available.</td>
                </tr>
              )}
              {vciTableRows.map((row) => (
                (() => {
                  const trustRow = latestTrustRowByCountry.get(getVciCountryKey(row)) || row;

                  return (
                    <tr
                      key={row.id || `${row.iso3}-${row.year}-${row.country}`}
                      className={getVciCountryKey(row) === selectedCountryKey ? 'is-selected' : ''}
                      aria-selected={getVciCountryKey(row) === selectedCountryKey}
                    >
                      <th scope="row">
                        <button type="button" onClick={() => setSelectedCountryKey(getVciCountryKey(row))}>
                          {row.country}
                        </button>
                      </th>
                      <td>{renderVciCell(trustRow, 'trust_government', false)}</td>
                      <td className="demand-vci-group-divider">{renderVciCell(trustRow, 'trust_healthcare', false)}</td>
                      <td>{row.year}</td>
                      <td>{renderVciCell(row, 'children')}</td>
                      <td>{renderVciCell(row, 'safe')}</td>
                      <td>{renderVciCell(row, 'effective')}</td>
                      <td>{renderVciCell(row, 'beliefs')}</td>
                    </tr>
                  );
                })()
              ))}
            </tbody>
          </table>
        </div>
        {selectedYearRows.length > 8 && (
          <div className="vaccines-matrix-footer">
            <p className="vaccines-footnote">
              Showing {vciTableRows.length} of {selectedYearRows.length} country rows{selectedYear ? ` for ${selectedYear}` : ''}.
            </p>
            <button
              type="button"
              className="vaccines-expand-button"
              onClick={() => setShowAllVciRows((current) => !current)}
            >
              {showAllVciRows ? 'Show fewer rows' : `Show all ${selectedYearRows.length} rows`}
            </button>
          </div>
        )}
        <p className="vaccines-table-footnote">
          Sources:{' '}
          <a href="https://www.vaccineconfidence.org/vci/data-and-methodology/" target="_blank" rel="noreferrer" className="vaccines-source-link">
            Vaccine Confidence Index Data & Methodology
          </a>
          {' '}and healthcare trust data from{' '}
          <a href="https://wellcome.org/insights/reports/wellcome-global-monitor/2018/chapter-3-trust-science-and-health-professionals" target="_blank" rel="noreferrer" className="vaccines-source-link">
            Wellcome Global Monitor 2020
          </a>.
        </p>
      </div>

      <div className="vaccines-schedule-card budgets-ta-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Targeted TA</p>
            <h3 className="vaccines-card-title">Immunization - SBC TA Hub requests</h3>
          </div>
          <p className="budgets-data-note">
            {isTaLoading
              ? 'Loading TA Hub requests...'
              : !selectedCountryRow
                ? 'Select a country in the table above'
                : `${demandTaRequests.length} request${demandTaRequests.length === 1 ? '' : 's'} for ${selectedCountryDisplayName}`}
          </p>
        </div>

        {taError && <p className="budgets-error-note">{taError}</p>}
        {!isTaLoading && !taError && !selectedCountryRow && (
          <p className="budgets-data-note">Click a country in the table above to view Immunization - SBC TA requests for that country.</p>
        )}

        <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
          <table className="vaccines-wuenic-table budgets-table budgets-ta-table" aria-label="Immunization - SBC TA Hub requests">
            <thead>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">Description</th>
                <th scope="col">Status</th>
                <th scope="col">TA Lead Staff</th>
                <th scope="col">Lead CoE Team</th>
              </tr>
            </thead>
            <tbody>
              {isTaLoading && (
                <tr>
                  <td colSpan="5">Loading Immunization - SBC TA requests...</td>
                </tr>
              )}
              {!isTaLoading && !taError && selectedCountryRow && demandTaRequests.length === 0 && (
                <tr>
                  <td colSpan="5">No Immunization - SBC TA requests found for {selectedCountryDisplayName}.</td>
                </tr>
              )}
              {!isTaLoading && demandTaRequests.map((row, index) => {
                const requestKey = getTaRequestKey(row, index, taTitleColumnKey);
                const description = getRowText(row, taDescriptionColumnKey) || getRowText(row, taTitleColumnKey);
                const isExpanded = Boolean(expandedTaRequestKeys[requestKey]);
                const shouldTruncate = description.length > 200;
                const visibleDescription = shouldTruncate && !isExpanded
                  ? `${description.slice(0, 200).trimEnd()}...`
                  : description;

                return (
                  <tr key={requestKey}>
                    <td>{getRowText(row, taCountryColumnKey) || 'N/A'}</td>
                    <td>
                      {visibleDescription || 'N/A'}
                      {shouldTruncate && (
                        <button
                          type="button"
                          className="budgets-ta-read-more-button"
                          onClick={() => toggleTaRequestExpanded(requestKey)}
                        >
                          {isExpanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </td>
                    <td>{getRowText(row, taStatusColumnKey) || 'N/A'}</td>
                    <td>{getRowText(row, taLeadStaffColumnKey) || 'N/A'}</td>
                    <td>
                      <span className="budgets-ta-priority">{getRowText(row, taLeadCoeTeamColumnKey) || DEMAND_TA_TEAM_NAME}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="vaccines-schedule-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Training</p>
            <h3 className="vaccines-card-title">Social listening training links</h3>
          </div>
        </div>

        <div className="social-training-grid">
          {socialListeningTrainingLinks.map((link) => {
            const Icon = link.icon;

            return (
              <a key={link.key} href={link.url} target="_blank" rel="noreferrer" className={`social-training-card social-training-${link.key}`}>
                <span className="social-training-graphic" aria-hidden="true">
                  <Icon />
                </span>
                <span className="social-training-content">
                  <span className="social-training-format">{link.format}</span>
                  <span className="social-training-title">{link.title}</span>
                  <span className="social-training-description">{link.description}</span>
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const DemandVaccineTrust = ({ userClaims, isEmbedded = false }) => {
  if (isEmbedded) {
    return <DemandVaccineTrustContent />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="demand-vaccine-trust" userClaims={userClaims} />
      <div className="v2-module p-4">
        <DemandVaccineTrustContent />
      </div>
    </div>
  );
};

export default DemandVaccineTrust;
